import React, { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@material-ui/core";

import { StatusBadge } from "../../components/ui";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { getTokens } from "../../styles/tokens";

const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 5000;
const SEARCH_DELAY_MS = 400;
const FILTERS = ["all", "sent", "failed", "skipped", "pending"];

const t = (key) => i18n.t(`invoiceCampaigns.details.${key}`);

const STATUS_COLOR = {
  sent: "success",
  failed: "error",
  pending: "warning",
};

export const formatPhone = (number) => {
  const digits = String(number || "").replace(/\D/g, "");
  if (!digits) return "-";
  const br = digits.match(/^55(\d{2})(\d{4,5})(\d{4})$/);
  if (br) return `(${br[1]}) ${br[2]}-${br[3]}`;
  return `+${digits}`;
};

/** Texto do resultado de um item: ignoradas têm um rótulo por motivo. */
const statusLabel = (status) => t(`status.${status}`);

/** Motivo da falha/ignorada e o aviso de um envio feito (ex.: PDF não enviado). */
const reasonOf = (item) => item.error || item.warning || (String(item.status).startsWith("skipped_") ? t(`reason.${item.status}`) : "");

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    toolbar: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 8,
      marginBottom: 12,
    },
    search: { flex: "1 1 240px", minWidth: 200 },
    chips: { display: "flex", flexWrap: "wrap", gap: 6 },
    tableWrap: { overflowX: "auto" },
    nowrap: { whiteSpace: "nowrap" },
    name: { minWidth: 180, fontWeight: 500 },
    reason: { minWidth: 200, color: tk.textSecondary, fontSize: 12.5 },
    alt: { display: "block", fontSize: 11.5, color: tk.textMuted },
    state: { padding: 32, textAlign: "center", color: tk.textMuted },
  };
});

const CampaignDetailsDialog = ({ campaign, initialFilter, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const { datetimeToClient } = useDate();

  const [filter, setFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const requestRef = useRef(0);
  const campaignId = campaign?.id;

  useEffect(() => {
    if (!campaignId) return;
    setFilter(initialFilter || "all");
    setSearchText("");
    setSearch("");
    setPage(0);
    setData(null);
  }, [campaignId, initialFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(0);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchPage = useCallback(
    (params) =>
      api.get(`/isp/invoice-campaigns/${campaignId}/items`, {
        params: { status: filter === "all" ? undefined : filter, search: search || undefined, ...params },
      }),
    [campaignId, filter, search]
  );

  useEffect(() => {
    if (!campaignId) return undefined;
    const requestId = ++requestRef.current;
    setLoading(true);
    fetchPage({ pageNumber: page + 1, pageSize: PAGE_SIZE })
      .then(({ data: result }) => {
        if (requestRef.current === requestId) setData(result);
      })
      .catch((err) => {
        if (requestRef.current === requestId) toastError(err);
      })
      .finally(() => {
        if (requestRef.current === requestId) setLoading(false);
      });
    return () => {
      requestRef.current += 1;
    };
  }, [campaignId, page, fetchPage]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data: result } = await fetchPage({ pageNumber: 1, pageSize: EXPORT_PAGE_SIZE });
      if (!result.items.length) {
        toast.info(t("empty"));
        return;
      }
      const sheetRows = result.items.map((item) => ({
        [t("columns.customer")]: item.customerName,
        [t("columns.contract")]: item.contractCode,
        [t("columns.invoice")]: item.erpInvoiceId,
        [t("columns.dueDate")]: item.dueDate || "",
        [t("columns.amount")]: item.amount || "",
        [t("columns.number")]: item.contactNumber ? formatPhone(item.contactNumber) : "",
        [t("columns.otherNumbers")]: item.alternateNumbers.map(formatPhone).join(", "),
        [t("columns.result")]: statusLabel(item.status),
        [t("columns.reason")]: reasonOf(item),
        [t("columns.sentAt")]: item.sentAt ? datetimeToClient(item.sentAt) : "",
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheetRows), t("sheetName"));
      const safeName = String(campaign.name || "campanha").replace(/[^\w\- ]+/g, "_");
      XLSX.writeFile(workbook, `${safeName}.xlsx`);
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  const items = data?.items || [];
  const counts = data?.counts;

  return (
    <Dialog
      open={!!campaign}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      scroll="paper"
      aria-labelledby="invoice-campaign-details-title"
    >
      <DialogTitle id="invoice-campaign-details-title">{campaign?.name}</DialogTitle>
      <DialogContent dividers>
        <div className={classes.toolbar}>
          <TextField
            className={classes.search}
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t("searchPlaceholder")}
            inputProps={{ "aria-label": t("searchPlaceholder") }}
          />
          <div className={classes.chips}>
            {FILTERS.map((key) => (
              <Chip
                key={key}
                size="small"
                clickable
                color={filter === key ? "primary" : "default"}
                label={`${t(`filters.${key}`)}${counts ? ` (${counts[key]})` : ""}`}
                onClick={() => {
                  setFilter(key);
                  setPage(0);
                }}
              />
            ))}
          </div>
        </div>

        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("columns.customer")}</TableCell>
                <TableCell>{t("columns.contract")}</TableCell>
                <TableCell>{t("columns.invoice")}</TableCell>
                <TableCell>{t("columns.dueDate")}</TableCell>
                <TableCell align="right">{t("columns.amount")}</TableCell>
                <TableCell>{t("columns.number")}</TableCell>
                <TableCell>{t("columns.result")}</TableCell>
                <TableCell>{t("columns.reason")}</TableCell>
                <TableCell>{t("columns.sentAt")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className={classes.name}>{item.customerName || "-"}</TableCell>
                  <TableCell className={classes.nowrap}>{item.contractCode || "-"}</TableCell>
                  <TableCell className={classes.nowrap}>{item.erpInvoiceId || "-"}</TableCell>
                  <TableCell className={classes.nowrap}>{item.dueDate || "-"}</TableCell>
                  <TableCell align="right" className={classes.nowrap}>{item.amount || "-"}</TableCell>
                  <TableCell className={classes.nowrap}>
                    {formatPhone(item.contactNumber)}
                    {item.status === "failed" && item.alternateNumbers.length > 0 && (
                      <span className={classes.alt}>
                        {t("alsoTried")} {item.alternateNumbers.map(formatPhone).join(", ")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={classes.nowrap}>
                    <StatusBadge color={STATUS_COLOR[item.status] || "neutral"} label={statusLabel(item.status)} />
                  </TableCell>
                  <TableCell className={classes.reason}>{reasonOf(item) || "-"}</TableCell>
                  <TableCell className={classes.nowrap}>{item.sentAt ? datetimeToClient(item.sentAt) : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {loading && !items.length && (
            <div className={classes.state}>
              <CircularProgress size={24} />
            </div>
          )}
          {!loading && data && !items.length && <div className={classes.state}>{t("empty")}</div>}
        </div>

        <TablePagination
          component="div"
          count={data?.count || 0}
          page={page}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, next) => setPage(next)}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("of")} ${count}`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleExport} disabled={exporting || !items.length} variant="outlined">
          {exporting ? t("exporting") : t("export")}
        </Button>
        <Button onClick={onClose} color="primary" variant="contained">
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignDetailsDialog;
